import type { DeepNonNullable } from 'ts-essentials';
import { proxy } from 'valtio';

import type { RequireAtLeastOne } from '@/utils/tsutils';

import type { UserFragment } from '@/graphql/saleor/api';

//TODO add a status isInitialized
export interface UserState<Require = boolean> {
  isAuthenticated: null | boolean;
  isAuthenticating: boolean;
  isInitialized: boolean;
  isInitializing: boolean;
  isProcessing: boolean;
  user: Require extends false
    ? Partial<UserFragment> | null
    : DeepNonNullable<UserFragment>;
}

export const initialUserState: UserState = {
  user: null,
  isProcessing: false,
  isInitializing: false,
  isAuthenticated: null,
  isAuthenticating: false,
  isInitialized: false,
};

export const userState = proxy<UserState>({ ...initialUserState });

export const userActions = {
  setUser: (data: UserState['user']) => {
    userState.user = data;
    userState.isAuthenticated = !!data;
  },

  setAuthenticated: ({
    isAuthenticated,
    isAuthenticating,
  }: RequireAtLeastOne<
    Pick<UserState, 'isAuthenticated' | 'isAuthenticating'>
  >) => {
    if (
      isAuthenticated !== undefined &&
      isAuthenticated !== userState.isAuthenticated
    ) {
      userState.isAuthenticated = isAuthenticated;
    }

    if (
      isAuthenticating !== undefined &&
      isAuthenticating !== userState.isAuthenticating
    ) {
      userState.isAuthenticating = isAuthenticating;
    }
  },

  updateProcessingState: ({
    initializing,
    isInitialized,
    processing,
  }: RequireAtLeastOne<{
    initializing: boolean;
    isInitialized?: boolean;
    processing: boolean;
  }>) => {
    if (processing !== undefined && processing !== userState.isProcessing) {
      userState.isProcessing = processing;
    }

    if (
      initializing !== undefined &&
      initializing !== userState.isInitializing
    ) {
      userState.isInitializing = initializing;
    }

    if (
      isInitialized !== undefined &&
      isInitialized !== null &&
      isInitialized !== userState.isInitialized
    ) {
      userState.isInitialized = isInitialized;
    }
  },

  clear: async () => {
    const set = <K extends keyof UserState>(k: K, v: UserState[K]) => {
      userState[k] = v;
    };
    (Object.keys(initialUserState) as (keyof UserState)[]).forEach((key) => {
      set(key, initialUserState[key]);
    });
  },
};
